/**
 * Shared wake-word helpers for room companions.
 *
 * The runtime wake word is the effective companion display name for the room:
 * host custom_name override when present, otherwise the companion default name.
 */

import { readMusicTail } from './MusicSpeech.js';

export interface CompanionDirectivePhrases {
  wakeWords: string[];
  stopWords: string[];
  sleepWords: string[];
}

export interface BuildCompanionDirectivePhraseOptions {
  /**
   * Include legacy Tele/Telly aliases. This should normally be false when a
   * room has a concrete companion name, and true only as a fallback.
   */
  includeLegacyAliases?: boolean;
}

export interface WakeWordMatch {
  phrase: string;
  normalizedPhrase: string;
  start: number;
  end: number;
}

export const COMPANION_DIRECTIVE_CONFIG_INVALIDATION_CHANNEL = 'companion:config:invalidate';

interface NormalizedText {
  normalized: string;
  startMap: number[];
  endMap: number[];
}

const WORD_CHAR_PATTERN = /[\p{L}\p{N}]/u;

export const LEGACY_TELE_WAKE_WORDS = [
  'kellie',
  'telly',
  'tele',
  'telle',
  'tilly',
  'tully',
  'tg',
] as const;

const LEGACY_TELE_STOP_WORDS = [
  'telly stop',
  'tele stop',
  'telle stop',
  'tilly stop',
  'tully stop',
  'tg stop',
  'stop telly',
  'stop tele',
  'stop telle',
  'stop tilly',
  'stop tully',
  'stop tg',
] as const;

const LEGACY_TELE_SLEEP_WORDS = [
  'telly sleep',
  'tele sleep',
  'telle sleep',
  'tilly sleep',
  'tully sleep',
  'tg sleep',
  'sleep telly',
  'sleep tele',
  'sleep telle',
  'sleep tilly',
  'sleep tully',
  'sleep tg',
  'telly goodbye',
  'tele goodbye',
  'telle goodbye',
  'tilly goodbye',
  'tully goodbye',
  'tg goodbye',
  'goodbye telly',
  'goodbye tele',
  'goodbye telle',
  'goodbye tilly',
  'goodbye tully',
  'goodbye tg',
  'telly go to sleep',
  'tele go to sleep',
  'telle go to sleep',
  'tilly go to sleep',
  'tully go to sleep',
  'tg go to sleep',
  'telly goodnight',
  'tele goodnight',
  'telle goodnight',
  'tilly goodnight',
  'tully goodnight',
  'tg goodnight',
] as const;

const COMMON_NAME_SPEECH_ALIASES: Record<string, readonly string[]> = {
  vinny: ['vinnie', 'vinney', 'benny'],
};

function appendUnique(target: string[], value: string): void {
  const normalized = normalizeWakePhrase(value);
  if (!normalized) return;
  if (!target.some((existing) => normalizeWakePhrase(existing) === normalized)) {
    target.push(value.trim());
  }
}

function buildDynamicNamePhrases(companionName: string | null | undefined): string[] {
  const trimmed = companionName?.trim();
  if (!trimmed) return [];
  const normalized = normalizeWakePhrase(trimmed);
  if (!normalized) return [];

  const names = [trimmed];
  if (normalized === 'tele' || normalized === 'telly' || normalized.includes(' ')) {
    return names;
  }

  for (const alias of buildNameSpeechAliases(normalized)) {
    appendUnique(names, alias);
  }
  return names;
}

function isTeleLikeName(companionName: string | null | undefined): boolean {
  const normalized = normalizeWakePhrase(companionName ?? '');
  return normalized === 'tele' || normalized === 'telly';
}

function buildNameSpeechAliases(normalizedName: string): string[] {
  const aliases: string[] = [];

  for (const alias of COMMON_NAME_SPEECH_ALIASES[normalizedName] ?? []) {
    appendUnique(aliases, alias);
  }

  if (normalizedName.endsWith('y') && normalizedName.length > 2) {
    const stem = normalizedName.slice(0, -1);
    appendUnique(aliases, `${stem}ie`);
    appendUnique(aliases, `${stem}ey`);
  } else if (normalizedName.endsWith('ie') && normalizedName.length > 3) {
    const stem = normalizedName.slice(0, -2);
    appendUnique(aliases, `${stem}y`);
    appendUnique(aliases, `${stem}ey`);
  } else if (normalizedName.endsWith('ey') && normalizedName.length > 3) {
    const stem = normalizedName.slice(0, -2);
    appendUnique(aliases, `${stem}y`);
    appendUnique(aliases, `${stem}ie`);
  }

  return aliases;
}

export function buildCompanionDirectivePhrases(
  companionName: string | null | undefined,
  options: BuildCompanionDirectivePhraseOptions = {},
): CompanionDirectivePhrases {
  const names = buildDynamicNamePhrases(companionName);
  const includeLegacyAliases = options.includeLegacyAliases === true
    || names.length === 0
    || isTeleLikeName(companionName);

  const wakeWords: string[] = [];
  const stopWords: string[] = [];
  const sleepWords: string[] = [];

  for (const name of names) {
    appendUnique(wakeWords, name);
    appendUnique(stopWords, `${name} stop`);
    appendUnique(stopWords, `stop ${name}`);
    appendUnique(sleepWords, `${name} sleep`);
    appendUnique(sleepWords, `sleep ${name}`);
    appendUnique(sleepWords, `${name} goodbye`);
    appendUnique(sleepWords, `goodbye ${name}`);
    appendUnique(sleepWords, `${name} go to sleep`);
    appendUnique(sleepWords, `${name} goodnight`);
  }

  if (includeLegacyAliases) {
    for (const phrase of LEGACY_TELE_WAKE_WORDS) appendUnique(wakeWords, phrase);
    for (const phrase of LEGACY_TELE_STOP_WORDS) appendUnique(stopWords, phrase);
    for (const phrase of LEGACY_TELE_SLEEP_WORDS) appendUnique(sleepWords, phrase);
  }

  return {
    wakeWords: sortPhrasesForMatching(wakeWords),
    stopWords: sortPhrasesForMatching(stopWords),
    sleepWords: sortPhrasesForMatching(sleepWords),
  };
}

/**
 * "<name>, stop the music" is a request about the MUSIC, not the companion's STOP
 * directive (#4278). The stop phrase is "<name> stop", so without this the most
 * natural way to pause a song is heard as "stop talking": the turn ends with no
 * response and the music plays on.
 *
 * STOP has to stay dependable, so the exception is narrow and leans towards STOP:
 *   - it looks only at what DIRECTLY follows the matched "<name> stop" — a later
 *     "stop the music" in the same utterance ("Nova stop. I said stop the music!",
 *     "Nova, stop talking and stop the music") changes nothing;
 *   - "stop <name>" is never a music request: the name already followed the verb;
 *   - the music noun must END the request. Only what may follow any music command
 *     (readMusicTail — one list, shared with the classifier), the name again, or a
 *     new sentence may come after it. "nova stop the music is too loud i can't hear
 *     you" is someone who wants the companion to stop.
 * A request that passes is left to the wake word, which still interrupts a companion
 * that is speaking.
 */
const MUSIC_AFTER_STOP = /^[\s,]*(?:playing\s+)?(?:(?:the|this|that|these|those|our|my)\s+)?(?:music|songs?|tracks?|playlists?|soundtrack|tunes?|mix)(?:\s+playback)?(?![\p{L}\p{N}'-])([^.!?;]*)/u;
export function isMusicStopRequest(message: string, stopMatch: WakeWordMatch): boolean {
  if (!stopMatch.normalizedPhrase.endsWith(' stop')) return false;
  // The match's indices belong to the NFKC-normalised text, not the raw one.
  const after = MUSIC_AFTER_STOP.exec(message.normalize('NFKC').slice(stopMatch.end).toLowerCase());
  if (!after) return false;
  const tail = after[1].replace(/[^\p{L}\p{N}\s%']/gu, ' ').replace(/\s+/g, ' ').trim();
  const name = stopMatch.normalizedPhrase.slice(0, -' stop'.length);
  return tail === name || readMusicTail(tail.endsWith(` ${name}`) ? tail.slice(0, -name.length) : tail) !== null;
}

export function normalizeWakePhrase(text: string): string {
  return normalizeWakeText(text).normalized;
}

export function findWakeWordMatch(message: string, phrases: readonly string[]): WakeWordMatch | null {
  const normalizedMessage = normalizeWakeText(message);
  if (!normalizedMessage.normalized) return null;

  let best: WakeWordMatch | null = null;
  for (const phrase of sortPhrasesForMatching(phrases)) {
    const normalizedPhrase = normalizeWakePhrase(phrase);
    if (!normalizedPhrase) continue;

    let searchFrom = 0;
    while (searchFrom <= normalizedMessage.normalized.length) {
      const matchStart = normalizedMessage.normalized.indexOf(normalizedPhrase, searchFrom);
      if (matchStart === -1) break;
      const matchEnd = matchStart + normalizedPhrase.length;
      searchFrom = matchStart + 1;

      if (!hasWordBoundary(normalizedMessage.normalized, matchStart, matchEnd)) {
        continue;
      }

      const start = normalizedMessage.startMap[matchStart] ?? 0;
      const end = normalizedMessage.endMap[matchEnd - 1] ?? message.length;
      const candidate = { phrase, normalizedPhrase, start, end };
      if (!best || candidate.start < best.start || (
        candidate.start === best.start && candidate.normalizedPhrase.length > best.normalizedPhrase.length
      )) {
        best = candidate;
      }
    }
  }

  return best;
}

export function extractCommandAfterWakeMatch(message: string, match: WakeWordMatch): string | null {
  // The match's indices belong to the NFKC-normalised text. Sliced from the raw string, "ok… Nova
  // next song" gave the command "ext song": "…" is one character raw and three normalised.
  let command = message.normalize('NFKC').slice(match.end).trim();
  command = command.replace(/^[.,!?;:]+\s*/, '').trim();
  if (!command || /^[.,!?;:]+$/.test(command)) return null;
  return command;
}

function sortPhrasesForMatching(phrases: readonly string[]): string[] {
  return [...phrases]
    .filter((phrase) => normalizeWakePhrase(phrase).length > 0)
    .sort((a, b) => normalizeWakePhrase(b).length - normalizeWakePhrase(a).length);
}

function hasWordBoundary(text: string, start: number, end: number): boolean {
  const before = start === 0 ? ' ' : text[start - 1];
  const after = end >= text.length ? ' ' : text[end];
  return before === ' ' && after === ' ';
}

function normalizeWakeText(text: string): NormalizedText {
  let normalized = '';
  const startMap: number[] = [];
  const endMap: number[] = [];
  let lastWasSpace = true;

  for (const match of text.normalize('NFKC').matchAll(/./gu)) {
    const char = match[0];
    const index = match.index ?? 0;
    const end = index + char.length;

    if (WORD_CHAR_PATTERN.test(char)) {
      const lower = char.toLocaleLowerCase();
      for (const lowerChar of Array.from(lower)) {
        normalized += lowerChar;
        startMap.push(index);
        endMap.push(end);
      }
      lastWasSpace = false;
    } else if (!lastWasSpace) {
      normalized += ' ';
      startMap.push(index);
      endMap.push(end);
      lastWasSpace = true;
    }
  }

  if (normalized.endsWith(' ')) {
    normalized = normalized.slice(0, -1);
    startMap.pop();
    endMap.pop();
  }

  return { normalized, startMap, endMap };
}
